// Angular's `| number` filter and the `isNaN` guard every numeric cell in the usage report
// is wrapped in. Both are small and both are load-bearing for the pixel diff.

/**
 * `{{ n | number }}` — grouped thousands, at most three fraction digits.
 *
 * ⚠️ NOT `String(n)`. The enrichment counters and every count in the table go through the
 * filter, so a tenant with four-figure numbers renders "1,200" upstream and "1200" without
 * this — a difference the diff sees on a screen whose whole content is numbers.
 *
 * `en-US` is named rather than left to the environment: Angular's default locale is en-US
 * unless a locale file is loaded, and this app loads none. Taking the runtime's locale would
 * make the output depend on the machine the capture ran on.
 */
export function formatCount(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 3 })
}

/**
 * Upstream's `ctrl.isNaN(num)`, which is `isNaN(num) || num == null` — so it is true for
 * `null` AND `undefined` as well as a real NaN.
 *
 * ⚠️ IT GUARDS A STRING CONCATENATION, NOT JUST A FORMAT. The rate cells render
 * `value + '%'`, which on an absent value is the text "undefined%". These rows come from an
 * aggregate query that can legitimately produce nulls, so the guard fires in practice.
 */
export function isMissingNumber(value: number | null | undefined): boolean {
  return value === null || value === undefined || Number.isNaN(value)
}

/** A count cell: the filter, or `N/A`. */
export function countCell(value: number | null | undefined): string {
  return isMissingNumber(value) ? 'N/A' : formatCount(value as number)
}

/**
 * A rate cell: `value + '%'`, or `N/A`.
 *
 * ⚠️ THE NUMBER IS NOT PUT THROUGH THE FILTER HERE and that is upstream's behaviour, not an
 * omission — the template reads `(user.candidatesMessageOpenedRate + '%')` with no `| number`,
 * so a rate renders its raw value where a count renders a grouped one.
 */
export function rateCell(value: number | null | undefined): string {
  return isMissingNumber(value) ? 'N/A' : `${String(value)}%`
}
